import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { WifiOff, RefreshCw } from 'lucide-react';
import { getConnection, subscribeConnection, onBack, ping, ConnectionState, isNoResponse } from '../services/connection';

/**
 * One strip at the top of the app that says, in order of what matters:
 *   - "Couldn't reach the app — retrying" while the server gives no answer
 *     (the api layer decides that; see services/connection.ts), and
 *   - "Something didn't load" when the server answered but a page's data
 *     failed, with a Retry that refetches exactly the queries that failed.
 * When the server comes back, everything that failed is refetched on its own,
 * so the page fills in without anyone having to reload.
 */
export default function ConnectionBanner() {
  const qc = useQueryClient();
  const [conn, setConn] = useState<ConnectionState>(getConnection());
  const [failedQueries, setFailedQueries] = useState(0);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => subscribeConnection(setConn), []);

  // Count the live queries that errored with an actual answer from the server.
  // No-answer errors belong to the connection strip, not this one.
  useEffect(() => {
    const cache = qc.getQueryCache();
    const count = () => {
      const n = cache.getAll().filter(q =>
        q.state.status === 'error' && q.getObserversCount() > 0 && !isNoResponse(q.state.error)
      ).length;
      setFailedQueries(n);
    };
    count();
    return cache.subscribe(count);
  }, [qc]);

  // Server is back: reload whatever failed while it was away.
  useEffect(() => onBack(() => {
    qc.refetchQueries({ predicate: q => q.state.status === 'error' }).catch(() => {});
  }), [qc]);

  const retryFailed = async () => {
    setRetrying(true);
    try { await qc.refetchQueries({ predicate: q => q.state.status === 'error' }); }
    finally { setRetrying(false); }
  };

  if (!conn.reachable) {
    const longGone = conn.since != null && Date.now() - conn.since > 60_000;
    return (
      <div className="bg-amber-500 text-black text-xs font-semibold px-4 py-1.5 flex items-center justify-center gap-2 text-center" role="status">
        <WifiOff className="h-3.5 w-3.5 shrink-0" />
        <span>
          {longGone
            ? 'Still can’t reach the app — check Wi-Fi, or it may be mid-update. Retrying on its own.'
            : 'Couldn’t reach the app — retrying…'}
        </span>
        <button type="button" onClick={() => ping()} className="underline underline-offset-2 font-bold">Try now</button>
      </div>
    );
  }

  if (failedQueries > 0) {
    return (
      <div className="bg-red-600 text-white text-xs font-semibold px-4 py-1.5 flex items-center justify-center gap-2 text-center" role="status">
        <RefreshCw className={`h-3.5 w-3.5 shrink-0 ${retrying ? 'animate-spin' : ''}`} />
        <span>Something on this page didn’t load.</span>
        <button type="button" onClick={retryFailed} disabled={retrying} className="underline underline-offset-2 font-bold disabled:opacity-60">
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  return null;
}
